import { Form } from 'antd';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AuthFooterLink,
  AuthSubmitButton,
  PasswordField,
} from '@/components/auth';
import styles from '@/components/auth/AuthForm.module.scss';
import { ROUTES } from '@/constants/routes';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useNotify } from '@/hooks/useNotify';
import { useResetPasswordMutation } from '@/modules/auth/authApi';
import type { ResetPasswordPayload } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import {
  clearPasswordResetVerified,
  isPasswordResetVerified,
} from '@/utils/passwordResetSession';
import {
  confirmPasswordRules,
  PASSWORD_REUSED_MESSAGE,
  passwordFieldRules,
} from '@/utils/validation';

type ResetPasswordLocationState = { apiMessage?: string | null };

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const [searchParams] = useSearchParams();
  const [resetPassword, { isLoading, reset }] = useResetPasswordMutation();
  const emailFromQuery =
    searchParams.get('email') ?? searchParams.get('username') ?? '';

  const { control, handleSubmit, watch, setValue } = useForm<ResetPasswordPayload>({
    defaultValues: {
      username: emailFromQuery,
      password: '',
      confirmPassword: '',
    },
  });

  useMutationReset(reset);

  useEffect(() => {
    const apiMessage = (location.state as ResetPasswordLocationState | null)?.apiMessage;
    if (apiMessage) notify.success(apiMessage);
  }, [location.state, notify]);

  useEffect(() => {
    if (!emailFromQuery) {
      navigate(ROUTES.FORGOT_PASSWORD, { replace: true });
      return;
    }

    if (!isPasswordResetVerified(emailFromQuery)) {
      navigate(`${ROUTES.VERIFY_OTP}?email=${encodeURIComponent(emailFromQuery)}`, {
        replace: true,
      });
      return;
    }

    setValue('username', emailFromQuery);
  }, [emailFromQuery, navigate, setValue]);

  const onSubmit = async ({
    password,
    confirmPassword,
  }: Pick<ResetPasswordPayload, 'password' | 'confirmPassword'>) => {
    const result = await resolveMutationResult(
      resetPassword({
        username: emailFromQuery,
        password,
        confirmPassword,
      }),
    );

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    clearPasswordResetVerified();
    notify.success(result.message ?? 'Password updated successfully.');
    window.setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 2000);
  };

  if (!emailFromQuery || !isPasswordResetVerified(emailFromQuery)) return null;

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)} className={styles.form}>
      <p className={styles.passwordHint}>{PASSWORD_REUSED_MESSAGE}</p>
      <PasswordField
        control={control}
        name="password"
        label="New Password"
        rules={passwordFieldRules}
      />
      <PasswordField
        control={control}
        name="confirmPassword"
        label="Confirm Password"
        rules={confirmPasswordRules(() => watch('password'))}
      />
      <Form.Item>
        <AuthSubmitButton label="Update Password" loading={isLoading} />
      </Form.Item>
      <AuthFooterLink to={ROUTES.LOGIN} label="Back to login" />
    </Form>
  );
}
